(() => {
  'use strict';

  class FacetForm extends HTMLElement {
    constructor() {
      super();

      this.priceTimer = null;
      this.previewTimer = null;
      this.previewToken = 0;
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('[data-facet-form]');
      this.drawer = this.closest('facet-drawer');
      this.isDrawer = Boolean(this.drawer);
      this.applyCount = this.querySelector('[data-filter-apply-count]');
      this.filterCountBadge = this.querySelector('[data-filter-count-badge]');

      if (!this.form) {
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      this.form.addEventListener('change', this.handleChange, {
        signal: this.abortController.signal
      });

      this.form.addEventListener('input', this.handleInput, {
        signal: this.abortController.signal
      });

      this.form.addEventListener('submit', this.handleSubmit, {
        signal: this.abortController.signal
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.previewController?.abort();
      window.clearTimeout(this.priceTimer);
      window.clearTimeout(this.previewTimer);
    }

    handleChange(event) {
      this.updateFilterCountBadge();

      if (
        event.target.matches(
          '[data-price-input-min], [data-price-input-max], [data-price-range-min], [data-price-range-max]'
        )
      ) {
        return;
      }

      if (this.isDrawer) {
        this.schedulePreview();
        return;
      }

      this.apply();
    }

    handleInput(event) {
      if (
        !event.target.matches(
          '[data-price-input-min], [data-price-input-max], [data-price-range-min], [data-price-range-max]'
        )
      ) {
        return;
      }

      this.updateFilterCountBadge();

      if (this.isDrawer) {
        this.schedulePreview();
        return;
      }

      window.clearTimeout(this.priceTimer);

      this.priceTimer = window.setTimeout(() => {
        this.apply();
      }, 450);
    }

    countActiveFilters() {
      if (!this.form) {
        return 0;
      }

      let count = this.form.querySelectorAll(
        'input[type="checkbox"]:checked, input[type="radio"]:checked'
      ).length;

      this.form.querySelectorAll('price-range').forEach((range) => {
        const minInput = range.querySelector('[data-price-input-min]');
        const maxInput = range.querySelector('[data-price-input-max]');

        if (!minInput || !maxInput) {
          return;
        }

        const minimum = Number(minInput.value || minInput.min || 0);
        const maximum = Number(maxInput.value || maxInput.max || 0);
        const defaultMinimum = Number(minInput.min || 0);
        const defaultMaximum = Number(maxInput.max || 0);

        if (
          (minInput.value && minimum > defaultMinimum) ||
          (maxInput.value && maximum < defaultMaximum)
        ) {
          count += 1;
        }
      });

      return count;
    }

    updateFilterCountBadge() {
      if (!this.filterCountBadge) {
        return;
      }

      const count = this.countActiveFilters();

      this.filterCountBadge.textContent = String(count);
      this.filterCountBadge.hidden = count === 0;
    }

    handleSubmit(event) {
      event.preventDefault();

      this.apply({
        closeDrawer: this.isDrawer
      });
    }

    schedulePreview() {
      window.clearTimeout(this.previewTimer);

      this.previewTimer = window.setTimeout(() => {
        this.previewCount();
      }, 300);
    }

    buildFacetUrl() {
      if (!this.form) {
        return null;
      }

      const formData = new FormData(this.form);

      this.form.querySelectorAll('price-range').forEach((range) => {
        const minInput = range.querySelector('[data-price-input-min]');
        const maxInput = range.querySelector('[data-price-input-max]');

        if (!minInput || !maxInput) {
          return;
        }

        const minimum = Number(
          minInput.value || minInput.min || 0
        );

        const maximum = Number(
          maxInput.value || maxInput.max || 0
        );

        const defaultMinimum = Number(
          minInput.min || 0
        );

        const defaultMaximum = Number(
          maxInput.max || 0
        );

        if (
          !minInput.value ||
          minimum <= defaultMinimum
        ) {
          formData.delete(minInput.name);
        }

        if (
          !maxInput.value ||
          maximum >= defaultMaximum
        ) {
          formData.delete(maxInput.name);
        }
      });

      const url = new URL(
        this.form.action,
        window.location.origin
      );

      for (const [name, value] of formData.entries()) {
        const normalizedValue = String(value).trim();

        if (normalizedValue !== '') {
          url.searchParams.append(name, normalizedValue);
        }
      }

      url.searchParams.delete('page');

      return url;
    }

    apply({ closeDrawer = false } = {}) {
      const url = this.buildFacetUrl();

      if (!url) {
        return;
      }

      const collection = this.closest(
        'tf-collection-products'
      );

      let updateRequest = null;

      if (
        collection &&
        typeof collection.updateCollection === 'function'
      ) {
        // The drawer locks body scroll while open, so scrolling the page
        // now (while it's still closing) would silently no-op. Scroll only
        // once the drawer has actually unlocked it - see below.
        updateRequest = collection.updateCollection(url, {
          mode: 'replace',
          historyMode: 'push',
          shouldFocus: !closeDrawer
        });
      } else {
        window.location.assign(url);
        return;
      }

      if (
        closeDrawer &&
        this.drawer &&
        typeof this.drawer.close === 'function'
      ) {
        const finishClose = () => {
          if (
            this.drawer &&
            this.drawer.isConnected
          ) {
            this.drawer.close();
          }

          // close() unlocks body scroll synchronously, so the page can only
          // actually move once this runs.
          if (
            collection &&
            typeof collection.focusResults === 'function'
          ) {
            collection.focusResults();
          }
        };

        if (
          updateRequest &&
          typeof updateRequest.finally === 'function'
        ) {
          updateRequest.finally(finishClose);
        } else {
          finishClose();
        }
      }
    }

    async previewCount() {
      const sectionId = this.dataset.sectionId;

      if (!this.applyCount || !sectionId) {
        return;
      }

      const url = this.buildFacetUrl();

      if (!url) {
        return;
      }

      url.searchParams.set('section_id', sectionId);

      const token = ++this.previewToken;

      this.previewController?.abort();
      this.previewController = new AbortController();

      try {
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          signal: this.previewController.signal
        });

        if (!response.ok) {
          throw new Error(
            `Facet preview request failed: ${response.status}`
          );
        }

        const html = await response.text();

        if (token !== this.previewToken || !this.isConnected) {
          return;
        }

        const nextComponent = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(
            `tf-collection-products[data-section-id="${this.escapeSelector(
              sectionId
            )}"]`
          );

        if (
          !nextComponent ||
          nextComponent.dataset.resultsCount === undefined
        ) {
          throw new Error('Invalid facet preview response.');
        }

        this.applyCount.textContent =
          nextComponent.dataset.resultsCount;
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.warn(
          'Facet preview count failed:',
          sectionId,
          error
        );
      }
    }

    escapeSelector(value) {
      if (window.CSS?.escape) {
        return window.CSS.escape(String(value));
      }

      return String(value).replace(/["\\]/g, '\\$&');
    }
  }

  class FacetDrawer extends HTMLElement {
    constructor() {
      super();

      this.lastFocusedElement = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOpen = this.handleOpen.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.facet-drawer__panel');

      this.abortController?.abort();
      this.abortController = new AbortController();

      document.addEventListener('click', this.handleOpen, {
        signal: this.abortController.signal
      });

      this.addEventListener('click', this.handleClick, {
        signal: this.abortController.signal
      });

      this.addEventListener('keydown', this.handleKeydown, {
        signal: this.abortController.signal
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.unlockBody();
    }

    handleOpen(event) {
      const trigger = event.target.closest(
        `[aria-controls="${this.id}"], [data-facet-drawer-open]`
      );

      if (!trigger) {
        return;
      }

      const controlledId = trigger.getAttribute('aria-controls');

      if (
        controlledId &&
        controlledId !== this.id
      ) {
        return;
      }

      event.preventDefault();
      this.open(trigger);
    }

    handleClick(event) {
      const closeButton = event.target.closest(
        '[data-facet-drawer-close]'
      );

      if (!closeButton) {
        return;
      }

      event.preventDefault();
      this.close();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = this.getFocusableElements();

      if (!focusableElements.length) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    open(trigger) {
      if (!this.hidden) {
        return;
      }

      this.lastFocusedElement =
        trigger || document.activeElement;

      this.hidden = false;

      window.requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.lockBody();

        document
          .querySelectorAll(`[aria-controls="${this.id}"]`)
          .forEach((button) => {
            button.setAttribute('aria-expanded', 'true');
          });

        const focusableElements = this.getFocusableElements();

        if (focusableElements.length) {
          focusableElements[0].focus();
        }
      });
    }

    close() {
      if (this.hidden) {
        return;
      }

      this.classList.remove('is-open');
      this.unlockBody();

      document
        .querySelectorAll(`[aria-controls="${this.id}"]`)
        .forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });

      window.setTimeout(() => {
        this.hidden = true;

        if (
          this.lastFocusedElement &&
          document.contains(this.lastFocusedElement)
        ) {
          this.lastFocusedElement.focus();
        }
      }, 250);
    }

    getFocusableElements() {
      return [
        ...this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        )
      ].filter((element) => {
        return (
          !element.hidden &&
          element.offsetParent !== null
        );
      });
    }

    lockBody() {
      document.documentElement.classList.add(
        'facet-drawer-open'
      );
    }

    unlockBody() {
      document.documentElement.classList.remove(
        'facet-drawer-open'
      );
    }
  }

  class PriceRange extends HTMLElement {
    constructor() {
      super();

      this.handleRangeInput =
        this.handleRangeInput.bind(this);

      this.handleNumberInput =
        this.handleNumberInput.bind(this);
    }

    connectedCallback() {
      this.rangeMin = this.querySelector(
        '[data-price-range-min]'
      );

      this.rangeMax = this.querySelector(
        '[data-price-range-max]'
      );

      this.inputMin = this.querySelector(
        '[data-price-input-min]'
      );

      this.inputMax = this.querySelector(
        '[data-price-input-max]'
      );

      if (
        !this.rangeMin ||
        !this.rangeMax ||
        !this.inputMin ||
        !this.inputMax
      ) {
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      this.rangeMin.addEventListener(
        'input',
        this.handleRangeInput,
        {
          signal: this.abortController.signal
        }
      );

      this.rangeMax.addEventListener(
        'input',
        this.handleRangeInput,
        {
          signal: this.abortController.signal
        }
      );

      this.inputMin.addEventListener(
        'input',
        this.handleNumberInput,
        {
          signal: this.abortController.signal
        }
      );

      this.inputMax.addEventListener(
        'input',
        this.handleNumberInput,
        {
          signal: this.abortController.signal
        }
      );

      this.update();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    handleRangeInput(event) {
      const minimum = Number(this.rangeMin.value);
      const maximum = Number(this.rangeMax.value);

      if (
        event.target === this.rangeMin &&
        minimum > maximum
      ) {
        this.rangeMin.value = String(maximum);
      }

      if (
        event.target === this.rangeMax &&
        maximum < minimum
      ) {
        this.rangeMax.value = String(minimum);
      }

      this.inputMin.value = this.rangeMin.value;
      this.inputMax.value = this.rangeMax.value;

      this.update();
    }

    handleNumberInput() {
      const rangeMaximum = Number(
        this.rangeMax.max
      );

      const minimum = this.inputMin.value === ''
        ? 0
        : Math.max(
            0,
            Math.min(
              Number(this.inputMin.value),
              rangeMaximum
            )
          );

      const maximum = this.inputMax.value === ''
        ? rangeMaximum
        : Math.max(
            minimum,
            Math.min(
              Number(this.inputMax.value),
              rangeMaximum
            )
          );

      this.rangeMin.value = String(minimum);
      this.rangeMax.value = String(maximum);

      this.update();
    }

    update() {
      const rangeMaximum =
        Number(this.rangeMax.max) || 1;

      const minimum =
        Number(this.rangeMin.value);

      const maximum =
        Number(this.rangeMax.value);

      this.style.setProperty(
        '--price-min-percent',
        `${(minimum / rangeMaximum) * 100}%`
      );

      this.style.setProperty(
        '--price-max-percent',
        `${(maximum / rangeMaximum) * 100}%`
      );

      this.inputMin.max =
        String(maximum);

      this.inputMax.min =
        String(minimum);
    }
  }

  if (!customElements.get('facet-form')) {
    customElements.define(
      'facet-form',
      FacetForm
    );
  }

  if (!customElements.get('facet-drawer')) {
    customElements.define(
      'facet-drawer',
      FacetDrawer
    );
  }

  if (!customElements.get('price-range')) {
    customElements.define(
      'price-range',
      PriceRange
    );
  }
})();
